import { TableHeader, TableRow, TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { flexRender, Table } from "@tanstack/react-table";
import { useDataTableFeatures } from "../hooks/useDataTableFeatures";
import ColumnResizeHandle from "./ColumnResizeHandle";
import DataTableHead from "./DataTableHead";
import DataTableHeaderFilter from "./DataTableHeaderFilter";
import { COLUMN_SELECT_ID } from "@/constants";

interface DataTableHeaderProps<TData> {
  table: Table<TData>;
}

const DataTableHeader = <TData extends { id: string }>({
  table,
}: DataTableHeaderProps<TData>) => {
  const { enableFilters } = useDataTableFeatures();

  return (
    <TableHeader
      className={cn("grid sticky top-0 z-30 backdrop-blur-md bg-slate-100/70", {
        "h-[96px]": enableFilters,
        "h-[48px]": !enableFilters,
      })}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="flex w-full">
          {headerGroup.headers.map((header) =>
            header.column.id === COLUMN_SELECT_ID ? (
              <TableHead
                key={header.id}
                className="relative group flex h-auto px-0 border-r sticky bg-slate-100 z-40"
                style={{
                  width: `calc(var(--header-${header.column.id}-size) * 1px)`,
                  left: `calc(var(--col-${header.column.id}-position) * 1px)`,
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableHead>
            ) : (
              <DataTableHead
                key={header.id}
                header={header}
                filter={<DataTableHeaderFilter header={header} />}
              >
                {header.column.getCanResize() && (
                  <ColumnResizeHandle
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    columnId={header.column.id}
                  />
                )}
              </DataTableHead>
            ),
          )}
        </TableRow>
      ))}
    </TableHeader>
  );
};

DataTableHeader.displayName = "DataTableHeader";

export default DataTableHeader;
